import { action }                    from 'mobx';
import { lngLatToMercator }          from '@tokamap/core';
import { mercatorToLngLat }          from '@tokamap/core';
import type { TokaMapInstance }      from '@tokamap/core';
import { computed }                  from 'mobx';
import { makeObservable }            from 'mobx';
import { observable }                from 'mobx';
import { reaction }                  from 'mobx';
import { runInAction }               from 'mobx';
import currentEditorStore            from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore     from '@/TokamapEditor/stores/currentProjectEditorStore';
import { applyArbitrations }         from '@/TokamapEditor/helpers/planDoubts';
import { doubtKey }                  from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitration }      from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitrations }     from '@/TokamapEditor/helpers/planDoubts';
import type { PlanCorrections }      from '@/TokamapEditor/helpers/planDoubts';
import { applyPlaceTypeToSelection } from '@/TokamapEditor/helpers/planLayerMapping';
import { buildCategories }           from '@/TokamapEditor/helpers/planLayerMapping';
import { readyCategories }           from '@/TokamapEditor/helpers/planLayerMapping';
import { toggleAll }                 from '@/TokamapEditor/helpers/planLayerMapping';
import { toMappingPayload }          from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanCategory }         from '@/TokamapEditor/helpers/planLayerMapping';
import { buildDecor }                from '@/TokamapEditor/helpers/planDecor';
import { toDecorPayload }            from '@/TokamapEditor/helpers/planDecor';
import { isShapeEnabled }            from '@/TokamapEditor/helpers/planShapes';
import { shapeOwner }                from '@/TokamapEditor/helpers/planShapes';
import type { PlanDecor }            from '@/TokamapEditor/helpers/planDecor';
import { computeFallbackPlacement }  from '@/TokamapEditor/helpers/planBackground';
import { computePlacementFromShape } from '@/TokamapEditor/helpers/planBackground';
import { drawBackground }            from '@/TokamapEditor/helpers/planBackground';
import { findBackground }            from '@/TokamapEditor/helpers/planBackground';
import { placeBackground }           from '@/TokamapEditor/helpers/planBackground';
import { removeBackground }          from '@/TokamapEditor/helpers/planBackground';
import { apiErrorMessage }           from '@/TokamapEditor/planImport/planAnalysisApi';
import { createPlanAnalysis }        from '@/TokamapEditor/planImport/planAnalysisApi';
import { fetchMatchPreview }         from '@/TokamapEditor/planImport/planAnalysisApi';
import { fetchPlanPreview }          from '@/TokamapEditor/planImport/planAnalysisApi';
import { fetchPlanSource }           from '@/TokamapEditor/planImport/planAnalysisApi';
import { generatePlan }              from '@/TokamapEditor/planImport/planAnalysisApi';
import { launchDetection }           from '@/TokamapEditor/planImport/planAnalysisApi';
import { saveMapping }               from '@/TokamapEditor/planImport/planAnalysisApi';
import { waitForPlanDetection }      from '@/TokamapEditor/planImport/planDetectionWait';
import { reloadCurrentMap }          from '@/TokamapEditor/planImport/reloadCurrentMap';
import { rasterizeSvg }              from '@/TokamapEditor/planImport/rasterizeSvg';
import type { PlanDetectionProgress } from '@/TokamapEditor/planImport/planImportTypes';
import type { PlanDetectionResult }  from '@/TokamapEditor/planImport/planImportTypes';
import type { PlanGenerateReport }   from '@/TokamapEditor/planImport/planImportTypes';
import type { PlanPreviewResult }    from '@/TokamapEditor/planImport/planImportTypes';
import type { DxfPlacement }         from '@models/map/dxfTypes';
import type { PlanDoubt }            from '@models/map/dxfTypes';
import type { PlanMatchPreviewResult } from '@models/map/dxfTypes';
import type { PlanRegion }           from '@models/map/dxfTypes';
import type { PlanShape }            from '@models/map/dxfTypes';
import type { PlanSourceResult }     from '@models/map/dxfTypes';

export type PlanImportStep = 'file' | 'region' | 'review' | 'placement' | 'report';

const FRAME_MARGIN = 1.25;
const UNIVERSAL_MATCH_FIELDS = ['label', 'reference'];

function frameOnPlacement(instance: TokaMapInstance, placement: DxfPlacement, width: number, height: number) {
	const bounds = instance.mapAdapter.getViewportBounds();
	const [westX, southY] = lngLatToMercator([bounds.minX, bounds.minY]);
	const [eastX, northY] = lngLatToMercator([bounds.maxX, bounds.maxY]);
	const fit = Math.min(
		Math.abs(eastX - westX) / (placement.scale * width * FRAME_MARGIN),
		Math.abs(northY - southY) / (placement.scale * height * FRAME_MARGIN),
	);

	instance.mapAdapter.setCenter(mercatorToLngLat(placement.center));
	if (Number.isFinite(fit) && fit > 0) instance.mapAdapter.setZoom(instance.mapAdapter.getZoom() + Math.log2(fit));
}

export class PlanImportStore {
	@observable modalOpened = false;
	@observable step: PlanImportStep = 'file';
	@observable busy = false;
	@observable error: string | null = null;

	@observable.ref file: File | null = null;
	@observable analysisId: number | null = null;
	@observable.ref source: PlanSourceResult | null = null;
	@observable.ref region: PlanRegion | null = null;

	@observable detecting = false;
	@observable.ref progress: PlanDetectionProgress | null = null;
	@observable.ref detection: PlanDetectionResult | null = null;

	@observable.ref categories: PlanCategory[] = [];
	@observable.ref arbitrations: PlanArbitrations = {};
	@observable.ref corrections: PlanCorrections = {};
	@observable.ref decor: PlanDecor = {};
	@observable doubtIndex = 0;
	@observable.ref match: PlanMatchPreviewResult | null = null;
	@observable measuring = false;
	@observable.ref userDisabled = new Set<string>();
	@observable matchChoice: string | null = null;
	@observable generating: 'shapes' | 'reload' | null = null;

	@observable.ref preview: PlanPreviewResult | null = null;
	@observable backgroundId: string | null = null;
	@observable backgroundSrc = '';
	@observable.ref placement: DxfPlacement | null = null;
	@observable.ref report: PlanGenerateReport | null = null;

	private _detectionAbort: AbortController | null = null;
	private _manualMatch = new Set<string>();
	private _measuring: Promise<void> | null = null;
	private _stopWatchingBackground: (() => void) | null = null;

	constructor() {
		makeObservable(this);

		reaction(
			() => ('review' === this.step ? this.matchKey : ''),
			key => {
				if (key && '{}' !== key) void this.measureMatch();
			},
			{ delay: 400 },
		);
	}

	@computed get effectiveCategories(): PlanCategory[] {
		return applyArbitrations({
			categories: this.categories,
			shapes: this.detection?.shapes ?? [],
			arbitrations: this.arbitrations,
			corrections: this.corrections,
		});
	}

	@computed get importedCount(): number {
		const categories = readyCategories(this.effectiveCategories);

		return (this.detection?.shapes ?? []).filter(shape => isShapeEnabled(categories, shape.layer, shape.index)).length;
	}

	@computed get readyCount(): number {
		return readyCategories(this.effectiveCategories).length;
	}

	@action open() {
		this.reset();
		this.modalOpened = true;
	}

	@action reopenReview() {
		this._clearBackground();
		this.preview = null;
		this.step = 'review';
		this.modalOpened = true;
	}

	@action close() {
		this._detectionAbort?.abort();
		this._clearBackground();
		this.reset();
	}

	@action reset() {
		this.modalOpened = false;
		this.step = 'file';
		this.busy = false;
		this.error = null;
		this.file = null;
		this.analysisId = null;
		this.source = null;
		this.region = null;
		this.detecting = false;
		this.progress = null;
		this.preview = null;
		this.backgroundSrc = '';
		this.report = null;
		this._resetReview(null);
	}

	async start(file: File) {
		await this._run(async () => {
			const project = currentProjectEditorStore.get;
			const analysisId = await createPlanAnalysis({
				file,
				mapUrn: project.currentMap.urn,
				projectUrn: project.project.urn,
			});
			const source = await fetchPlanSource(analysisId);

			runInAction(() => {
				this.file = file;
				this.analysisId = analysisId;
				this.source = source;
				this.region = source.analysisRegion;
				this.step = 'region';
			});
		}, 'Le plan n’a pas pu être importé.');
	}

	@action setRegion(region: PlanRegion | null) {
		this.region = region;
	}

	@action backToRegion() {
		this._detectionAbort?.abort();
		this.step = 'region';
	}

	async detect() {
		const analysisId = this.analysisId;
		const source = this.source;
		if (!analysisId || !source) return;

		this._detectionAbort?.abort();
		const abort = new AbortController();
		this._detectionAbort = abort;

		runInAction(() => {
			this.detecting = true;
			this.progress = null;
			this.error = null;
		});

		try {
			const planDetectionId = await launchDetection(analysisId, this.region);
			const result = await waitForPlanDetection(planDetectionId, {
				signal: abort.signal,
				onProgress: progress => runInAction(() => {
					if (this._detectionAbort === abort) this.progress = progress;
				}),
			});
			if (abort.signal.aborted) return;

			runInAction(() => {
				this._resetReview({
					...result,
					source: { ...result.source, imageDataUri: source.imageDataUri },
					suggestedDecor: result.suggestedDecor ?? [],
					doubts: result.doubts ?? [],
					shapes: result.shapes ?? [],
					truncated: result.truncated ?? 0,
				});
				this.step = 'review';
			});
		} catch (e) {
			if (abort.signal.aborted) return;
			runInAction(() => this.error = apiErrorMessage(e, 'La détection des formes a échoué.'));
		} finally {
			runInAction(() => {
				if (this._detectionAbort === abort) {
					this.detecting = false;
					this._detectionAbort = null;
				}
			});
		}
	}

	@computed get activeDoubtIndexes(): number[] {
		return (this.detection?.doubts ?? []).flatMap((doubt, index) => {
			const owner = shapeOwner(this.categories, doubt.layer, doubt.index);

			return owner && this.userDisabled.has(owner.key) ? [] : [index];
		});
	}

	@computed get currentDoubtIndex(): number | null {
		const active = this.activeDoubtIndexes;

		return active.includes(this.doubtIndex) ? this.doubtIndex : (active[0] ?? null);
	}

	@computed get remainingDoubts(): number {
		return this.activeDoubtIndexes.filter(index => {
			const doubt = this.detection?.doubts[index];

			return doubt && undefined === this.arbitrations[doubtKey(doubt)];
		}).length;
	}

	@action arbitrate(shape: Pick<PlanShape, 'layer' | 'index'>, decision: PlanArbitration) {
		this.arbitrations = { ...this.arbitrations, [doubtKey(shape)]: decision };

		const doubts = this.detection?.doubts ?? [];
		const current = null === this.currentDoubtIndex ? undefined : doubts[this.currentDoubtIndex];
		if (current && doubtKey(current) === doubtKey(shape)) this.goToNextDoubt();
	}

	@action goToNextDoubt() {
		const doubts = this.detection?.doubts ?? [];
		const active = this.activeDoubtIndexes;
		const from = active.indexOf(this.currentDoubtIndex ?? -1);

		for (let step = 1; step <= active.length; step++) {
			const index = active[(from + step) % active.length]!;
			const doubt = doubts[index];
			if (doubt && undefined === this.arbitrations[doubtKey(doubt)]) {
				this.doubtIndex = index;

				return;
			}
		}
	}

	@action stepDoubt(offset: number) {
		const active = this.activeDoubtIndexes;
		if (!active.length) return;

		const from = Math.max(0, active.indexOf(this.currentDoubtIndex ?? -1));
		this.doubtIndex = active[(from + offset + active.length) % active.length]!;
	}

	@computed get manualDiscardCount(): number {
		return Object.values(this.arbitrations).filter(decision => 'discarded' === decision).length;
	}

	@action arbitrateMany(shapes: Pick<PlanShape, 'layer' | 'index'>[], decision: PlanArbitration) {
		this.arbitrations = { ...this.arbitrations, ...Object.fromEntries(shapes.map(shape => [doubtKey(shape), decision])) };
	}

	@action restoreManualDiscards() {
		this.arbitrations = Object.fromEntries(Object.entries(this.arbitrations).map(([key, decision]) => [key, 'discarded' === decision ? 'kept' : decision]));
	}

	@action correct(doubt: PlanDoubt, value: string) {
		this.corrections = { ...this.corrections, [doubtKey(doubt)]: value };
	}

	@action goToDoubt(index: number) {
		this.doubtIndex = index;
	}

	@action updateCategory(key: string, patch: Partial<PlanCategory>) {
		if (undefined !== patch.matchField) this._manualMatch.add(key);
		if (undefined !== patch.enabled) {
			const disabled = new Set(this.userDisabled);
			if (patch.enabled) disabled.delete(key);
			else disabled.add(key);
			this.userDisabled = disabled;
		}
		this.categories = this.categories.map(category => (category.key === key ? { ...category, ...patch } : category));
	}

	@action toggleAllCategories(enabled: boolean) {
		this.categories = toggleAll(this.categories, enabled);
		this.userDisabled = enabled ? new Set() : new Set(this.categories.map(category => category.key));
	}

	@action applyPlaceType(placeTypeUrn: string) {
		this.categories = applyPlaceTypeToSelection(this.categories, placeTypeUrn);
	}

	@computed get sharedPlaceType(): string | undefined {
		const types = new Set(this.categories.filter(category => category.enabled).map(category => category.placeTypeUrn));

		return 1 === types.size ? [...types][0] || undefined : undefined;
	}

	@computed get sharedMatchField(): string | undefined {
		const fields = new Set(readyCategories(this.effectiveCategories).map(category => category.matchField));

		return 1 === fields.size ? [...fields][0] || undefined : undefined;
	}

	@action setMatchFieldForAll(field: string) {
		const offered = new Set((this.match?.candidates ?? []).filter(candidate => candidate.field === field).map(candidate => candidate.category));
		const universal = UNIVERSAL_MATCH_FIELDS.includes(field);

		this.matchChoice = field;
		this.categories = this.categories.map(category => {
			if (!universal && !offered.has(category.key)) return category;
			this._manualMatch.add(category.key);

			return { ...category, matchField: field };
		});
	}

	@computed get matchKey(): string {
		return JSON.stringify(toMappingPayload(this.effectiveCategories).categoryPlaceTypeMapping);
	}

	async measureMatch() {
		const analysisId = this.analysisId;
		if (!analysisId || !this.readyCount) return;

		const measuring = (async () => {
			runInAction(() => this.measuring = true);

			try {
				await saveMapping(analysisId, this._mappingPayload());
				const match = await fetchMatchPreview(analysisId, this._mappingPayload().categoryPlaceTypeMapping);

				runInAction(() => {
					this.match = match;
					if (this.matchChoice) {
						this.setMatchFieldForAll(this.matchChoice);

						return;
					}

					this.categories = this.categories.map(category => {
						if (this._manualMatch.has(category.key)) return category;

						const recommended = match.candidates.find(candidate => candidate.category === category.key && candidate.recommended);

						return recommended ? { ...category, matchField: recommended.field } : category;
					});
				});
			} catch (e) {
				console.warn('Mesure du rapprochement impossible', e);
			} finally {
				runInAction(() => this.measuring = false);
			}
		})();

		this._measuring = measuring;
		await measuring;
	}

	async toPlacement(): Promise<boolean> {
		const analysisId = this.analysisId;
		if (!analysisId) return false;

		return this._run(async () => {
			await this._measuring;
			await saveMapping(analysisId, this._mappingPayload());
			const preview = await fetchPlanPreview(analysisId);
			const backgroundSrc = await rasterizeSvg(preview.svgDataUri, preview.width, preview.height)
				.catch(() => preview.svgDataUri);

			runInAction(() => {
				this.preview = preview;
				this.backgroundSrc = backgroundSrc;
				this.step = 'placement';
				this.modalOpened = false;
			});

			this.placeBackground(false);
		}, 'L’aperçu du plan n’a pas pu être préparé.');
	}

	@action placeBackground(atViewCenter: boolean) {
		const instance = currentEditorStore.get;
		const preview = this.preview;
		if (!instance || !preview || !this.analysisId) return;

		const georeferenced = !atViewCenter && preview.isGeoreferenced && preview.suggestedPlacement;
		const moved = atViewCenter && this.placement
			? { ...this.placement, center: lngLatToMercator(instance.getMapView().center) }
			: null;
		const target = georeferenced
			? preview.suggestedPlacement
			: moved ?? computeFallbackPlacement(instance, preview.width, preview.height);

		if (!target) {
			this.error = 'La vue de la carte n’est pas exploitable : tracez le plan à la main.';

			return;
		}

		this._clearBackground();
		this._watchBackground(placeBackground({
			instance,
			importId: this.analysisId,
			src: this.backgroundSrc || preview.svgDataUri,
			widthPx: preview.width,
			heightPx: preview.height,
			placement: target,
		}));

		if (georeferenced) frameOnPlacement(instance, target, preview.width, preview.height);
	}

	async drawBackgroundByHand() {
		const instance = currentEditorStore.get;
		const preview = this.preview;
		if (!instance || !preview || !this.analysisId) return;

		this._clearBackground();

		const drawnId = await drawBackground({
			instance,
			importId: this.analysisId,
			src: this.backgroundSrc || preview.svgDataUri,
			widthPx: preview.width,
			heightPx: preview.height,
		});

		if (drawnId) this._watchBackground(drawnId);
	}

	async generate() {
		const analysisId = this.analysisId;
		const instance = currentEditorStore.get;
		if (!analysisId || !instance) return;

		const placement = this.placement;
		if (!placement) {
			runInAction(() => this.error = 'Placez le plan sur la carte avant de générer les formes.');

			return;
		}

		runInAction(() => this.generating = 'shapes');

		try {
			await this._run(async () => {
				const report = await generatePlan(analysisId, placement);

				this._clearBackground();
				runInAction(() => {
					this.report = report;
					this.step = 'report';
					this.generating = 'reload';
				});

				await reloadCurrentMap(instance);
			}, 'La génération des formes a échoué.');
		} finally {
			runInAction(() => this.generating = null);
		}
	}

	@action private _resetReview(detection: PlanDetectionResult | null) {
		this.detection = detection;
		this.categories = detection ? buildCategories(detection.layers, detection.suggestedMapping, detection.shapes) : [];
		this.decor = buildDecor(detection?.suggestedDecor ?? []);
		this.arbitrations = {};
		this.corrections = {};
		this.doubtIndex = 0;
		this.match = null;
		this.matchChoice = null;
		this.userDisabled = new Set();
		this._manualMatch = new Set();
	}

	private _mappingPayload() {
		const categories = this.effectiveCategories;

		return {
			...toMappingPayload(categories),
			decorLayers: toDecorPayload(this.decor, categories),
		};
	}

	@action private _watchBackground(shapeId: string) {
		const instance = currentEditorStore.get;
		const preview = this.preview;
		if (!instance || !preview) return;

		this.backgroundId = shapeId;
		this._readPlacement();

		this._stopWatchingBackground?.();
		this._stopWatchingBackground = instance.shapeStore.onChange(() => this._readPlacement());
	}

	@action private _readPlacement() {
		const instance = currentEditorStore.get;
		const shape = instance && this.backgroundId ? findBackground(instance, this.backgroundId) : null;

		if (!shape || !this.preview) {
			this.backgroundId = null;
			this.placement = null;

			return;
		}

		this.placement = computePlacementFromShape(shape, this.preview.width);
	}

	@action private _clearBackground() {
		this._stopWatchingBackground?.();
		this._stopWatchingBackground = null;

		const instance = currentEditorStore.get;
		if (instance && this.backgroundId) removeBackground(instance, this.backgroundId);

		this.backgroundId = null;
		this.placement = null;
	}

	private async _run(task: () => Promise<void>, fallback: string): Promise<boolean> {
		runInAction(() => {
			this.busy = true;
			this.error = null;
		});

		try {
			await task();

			return true;
		} catch (e) {
			runInAction(() => this.error = apiErrorMessage(e, fallback));

			return false;
		} finally {
			runInAction(() => this.busy = false);
		}
	}
}

export default new PlanImportStore();
